import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Dialog } from '../../shared/custom-ui';
import { V2Button } from '../../shared/custom-ui/v2-components';
import { Icon } from '@ohif/ui';
import ApiServices from '../../shared/ApiServices/ApiServices';
import { toast } from 'react-toastify';
import FormComponent from './MemberForm';
import TeamLayout from './components/TeamLayout';
import ModernPageHeader from '../WorkListTable/components/ModernPageHeader';
import useWorklistScale from '../WorkListTable/hooks/useWorklistScale';
import { buildMemberDisplayName } from '../../shared/utils/nameUtils';
import RolesTab from './RolesTab';
import { Users, Shield, Search, X, ChevronLeft, ChevronRight, AlertTriangle, Trash2 } from 'lucide-react';
import PatientAvatar from '../WorkListTable/components/PatientAvatar';

const PAGE_LIMIT = 50;
type ActiveTab = 'members' | 'roles';

const TeamManagement: React.FC = () => {

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const activeTab: ActiveTab = searchParams.get('tab') === 'roles' ? 'roles' : 'members';

    const [memberSearch, setMemberSearch] = useState('');
    const [roleSearch, setRoleSearch] = useState('');

    const handleTabSwitch = (tab: ActiveTab) => {
        navigate(`/team${tab === 'roles' ? '?tab=roles' : ''}`, { replace: false });
    };

    const currentSearch = activeTab === 'members' ? memberSearch : roleSearch;
    const handleSearchChange = (v: string) => {
        if (activeTab === 'members') setMemberSearch(v);
        else setRoleSearch(v);
    };

    // ── Members state ────────────────────────────────────────────────────────
    const [teamMembers, setTeamMembers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formState, setFormState] = useState({ id: '', first_name: '', middle_name: '', last_name: '', email: '' });
    const [appointLoading, setAppointLoading] = useState(false);

    const [memberToDelete, setMemberToDelete] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // ── Cursor-based pagination ───────────────────────────────────────────────
    // keyHistory[i] = cursor passed to fetch page (i+1). keyHistory[0] = null (page 1).
    const [keyHistory, setKeyHistory] = useState<(string | null)[]>([null]);
    const [pageIndex, setPageIndex] = useState(0);          // 0-based
    const [nextKey, setNextKey] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState<number | null>(null);

    // Derived display helpers (no stale-closure risk)
    const currentPage = pageIndex + 1;
    const pageStart = pageIndex * PAGE_LIMIT + 1;
    const pageEnd = pageIndex * PAGE_LIMIT + teamMembers.length;

    // ── Roles for the member role selector ───────────────────────────────────
    const [roleNames, setRoleNames] = useState<string[]>([]);

    // Ref that lets us call RolesTab's openCreate from the page header
    const rolesCreateRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        fetchTeamMembers(null);
        fetchRoleNames();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Load role names once (used in the Role column dropdown) ──────────────
    const fetchRoleNames = async () => {
        try {
            const res = await ApiServices.getAllRoles();
            const list: any[] = Array.isArray(res) ? res : (res?.roles ?? res?.data ?? []);
            setRoleNames(list.map((r: any) => r.name ?? r.roleName ?? r.role ?? String(r)));
        } catch {
            // Silently fail — role selector just won't populate
        }
    };

    // ── Fetch members ─────────────────────────────────────────────────────────
    const fetchTeamMembers = async (cursor: string | null) => {
        setLoading(true);
        setFetchError(null);
        try {
            const response = await ApiServices.getTeamMember(PAGE_LIMIT, cursor);
            if (response?.members) {
                const members = response.members.map((member: any) => ({
                    user_id: member.user_id || '',
                    rad_id: member.rad_id || '',
                    email: member.email || '',
                    first_name: member.first_name || '',
                    middle_name: member.middle_name || '',
                    last_name: member.last_name || '',
                    name: buildMemberDisplayName(member),
                    role: member.role ?? member.roleName ?? undefined,
                    created_at: member.created_at || '',
                    updated_at: member.updated_at || '',
                }));
                setTeamMembers(members);
                setNextKey(response.pagination?.last_key ?? response.next_key ?? null);
                setHasMore(response.pagination?.has_more ?? response.has_more ?? false);
                if (response.count != null) setTotalCount(Number(response.count));
            }
        } catch (error: any) {
            const msg = error?.response?.data?.message || error?.message || 'Failed to fetch team members.';
            setFetchError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    // Go forward: add next cursor to history (only if not already cached)
    const handleNext = () => {
        if (!hasMore || !nextKey) return;
        const nextIdx = pageIndex + 1;
        setKeyHistory(prev => {
            if (prev.length > nextIdx) return prev; // already cached
            return [...prev, nextKey];
        });
        setPageIndex(nextIdx);
        fetchTeamMembers(nextKey);
    };

    // Go back: restore previous cursor from history
    const handlePrevious = () => {
        if (pageIndex === 0) return;
        const prevIdx = pageIndex - 1;
        setPageIndex(prevIdx);
        fetchTeamMembers(keyHistory[prevIdx]);
    };

    const openAddMemberModal = () => {
        setFormState({ id: '', first_name: '', middle_name: '', last_name: '', email: '' });
        setIsModalOpen(true);
    };

    const handleAddMember = async (data: { email: string; first_name?: string; middle_name?: string; last_name?: string }) => {
        if (!data.email) { toast.error('Please provide an email'); return; }
        setAppointLoading(true);
        try {
            const payload: Record<string, string> = { email: data.email };
            if (data.first_name?.trim()) payload.first_name = data.first_name.trim();
            if (data.middle_name?.trim()) payload.middle_name = data.middle_name.trim();
            if (data.last_name?.trim()) payload.last_name = data.last_name.trim();
            const response = await ApiServices.addTeamMember(payload);
            toast.success(response.message);
            fetchTeamMembers(null);
            setIsModalOpen(false);
        } catch (error: any) {
            toast.error(error.response?.data?.message);
        } finally {
            setAppointLoading(false);
        }
    };

    const handleDeleteMember = async () => {
        if (!memberToDelete) return;
        const { email, rad_id } = memberToDelete;
        if (!rad_id) { toast.error('Missing rad_id for this member.'); return; }
        setIsDeleting(true);
        try {
            const response = await ApiServices.deleteTeamMember(email, rad_id);
            toast.success(response.message);
            setTeamMembers(prev => prev.filter(member => member.rad_id !== rad_id));
            setMemberToDelete(null);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete member.');
        } finally {
            setIsDeleting(false);
        }
    };

    // ── Role assign / remove handlers ─────────────────────────────────────────
    const handleAssignRole = useCallback(async (email: string, role: string) => {
        try {
            await ApiServices.assignRoleToUser(email, role);
            toast.success(`Role "${role}" assigned`);
            setTeamMembers(prev => prev.map(m => m.email === email ? { ...m, role } : m));
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to assign role');
            throw err;
        }
    }, []);

    const handleRemoveRole = useCallback(async (email: string) => {
        try {
            await ApiServices.removeRoleFromUser(email);
            toast.success('Role removed');
            setTeamMembers(prev => prev.map(m => m.email === email ? { ...m, role: undefined } : m));
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? err?.message ?? 'Failed to remove role');
            throw err;
        }
    }, []);

    const paginationRef = useRef<HTMLDivElement>(null);
    const scale = useWorklistScale(paginationRef);

    // Hides pagination when search is active and no members match the query
    const hasVisibleMembers = memberSearch.trim()
        ? teamMembers.some(m =>
            `${m.name ?? ''} ${m.email ?? ''} ${m.role ?? ''}`
                .toLowerCase()
                .includes(memberSearch.trim().toLowerCase())
        )
        : teamMembers.length > 0;

    const pageTitle = activeTab === 'members' ? 'Team Members' : 'Roles & Permissions';
    const pageSubtitle = activeTab === 'members'
        ? 'Manage your team, invite new members and assign roles.'
        : 'Define roles and assign granular permissions to control access across the platform.';

    return (
        <div ref={paginationRef} className="relative">
            <ModernPageHeader
                title={pageTitle}
                subtitle={pageSubtitle}
                actions={
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {activeTab === 'members' ? (
                            <V2Button
                                variant="primary"
                                size="small"
                                leftIcon={<Icon name="add-plus-icon" className="h-4 w-4" />}
                                onClick={openAddMemberModal}
                            >
                                <span className="hidden md:inline">Add Member</span>
                                <span className="md:hidden">Add</span>
                            </V2Button>
                        ) : (
                            <V2Button
                                variant="primary"
                                size="small"
                                leftIcon={<Icon name="add-plus-icon" className="h-4 w-4" />}
                                onClick={() => rolesCreateRef.current?.()}
                            >
                                <span className="hidden md:inline">New Role</span>
                                <span className="md:hidden">New</span>
                            </V2Button>
                        )}
                    </div>
                }
            >

                <div className="relative w-full max-w-sm">
                    <Search
                        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4"
                        style={{ color: 'var(--v2-text-muted)' }}
                    />
                    <input
                        value={currentSearch}
                        onChange={e => handleSearchChange(e.target.value)}
                        placeholder={activeTab === 'members' ? 'Search members…' : 'Search roles…'}
                        aria-label={activeTab === 'members' ? 'Search members' : 'Search roles'}
                        className="w-full rounded-lg border border-[var(--v2-border-subtle)] bg-[var(--v2-bg-card-inner)] text-[var(--v2-text-primary)] pl-10 pr-9 py-2.5 text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[var(--v2-primary-500)]/30 placeholder:text-[var(--v2-text-muted)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] transition-colors"
                    />
                    {currentSearch && (
                        <button
                            type="button"
                            onClick={() => handleSearchChange('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-[var(--v2-text-muted)] hover:text-[var(--v2-text-heading)] transition-colors"
                            style={{ outline: 'none' }}
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </ModernPageHeader>

            {/* ══ TAB SWITCHER ROW ══ */}
            <div
                className="flex items-center mb-5"
                style={{ borderBottom: '1px solid var(--v2-border-subtle)' }}
            >
                {/* ── Underline Tab Switcher (left) ── */}
                <div className="flex items-center flex-shrink-0">
                    {(['members', 'roles'] as ActiveTab[]).map(tab => {
                        const isActive = activeTab === tab;
                        const TabIcon = tab === 'members' ? Users : Shield;
                        const label = tab === 'members' ? 'Members' : 'Roles & Permissions';
                        const count = tab === 'members' && teamMembers.length > 0 ? teamMembers.length : null;
                        return (
                            <button
                                key={tab}
                                onClick={() => handleTabSwitch(tab)}
                                className="relative flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium transition-colors duration-150"
                                style={{
                                    outline: 'none',
                                    color: isActive ? 'var(--v2-primary-600, #2563eb)' : 'var(--v2-text-muted)',
                                    borderBottom: isActive ? '2px solid var(--v2-primary-600, #2563eb)' : '2px solid transparent',
                                    marginBottom: '-1px', /* overlap the row border so active underline sits on top */
                                    background: 'transparent',
                                }}
                            >
                                <TabIcon className="w-3.5 h-3.5" strokeWidth={2} />
                                <span>{label}</span>
                                {count !== null && (
                                    <span
                                        className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none"
                                        style={{
                                            background: isActive
                                                ? 'var(--v2-primary-100, #dbeafe)'
                                                : 'var(--v2-bg-hover, rgba(0,0,0,0.05))',
                                            color: isActive
                                                ? 'var(--v2-primary-600, #2563eb)'
                                                : 'var(--v2-text-muted)',
                                        }}
                                    >
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

            </div>

            {/* ══ CONTENT ══ */}
            {activeTab === 'members' ? (
                <div>
                    <TeamLayout
                        data={teamMembers}
                        loading={loading}
                        error={fetchError ?? undefined}
                        onDelete={member => setMemberToDelete(member)}
                        scale={scale}
                        roles={roleNames}
                        onAssignRole={handleAssignRole}
                        onRemoveRole={handleRemoveRole}
                        onAddMember={openAddMemberModal}
                        onRetry={() => fetchTeamMembers(null)}
                        search={memberSearch}
                    />
                    {!loading && !fetchError && hasVisibleMembers && (
                        <div className="mt-4" style={{ zoom: scale }}>
                            {/* ══ PREMIUM PAGINATION BAR ══ */}
                            <div
                                className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl"
                                style={{
                                    background: 'var(--v2-bg-card)',
                                    border: '1px solid var(--v2-border-card)',
                                    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                                }}
                            >
                                {/* Left — record range */}
                                <div className="flex items-center gap-2 min-w-0">
                                    <div
                                        className="hidden sm:flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
                                        style={{ background: 'color-mix(in srgb, var(--v2-primary-500,#3b82f6) 10%, transparent)' }}
                                    >
                                        <Users className="w-3.5 h-3.5" style={{ color: 'var(--v2-primary-600,#2563eb)' }} />
                                    </div>
                                    <span className="text-[13px] font-medium whitespace-nowrap" style={{ color: 'var(--v2-text-muted)' }}>
                                        {totalCount != null ? (
                                            <>
                                                <span className="font-bold" style={{ color: 'var(--v2-text-heading)' }}>
                                                    {pageStart}–{pageEnd}
                                                </span>
                                                {' of '}
                                                <span className="font-bold" style={{ color: 'var(--v2-text-heading)' }}>
                                                    {totalCount}
                                                </span>
                                                {' members'}
                                            </>
                                        ) : (
                                            <><span className="font-bold" style={{ color: 'var(--v2-text-heading)' }}>{teamMembers.length}</span> members</>
                                        )}
                                    </span>
                                </div>

                                {/* Center — page pill */}
                                <div
                                    className="flex items-center gap-1.5 px-3 py-1 rounded-full flex-shrink-0"
                                    style={{
                                        background: 'var(--v2-bg-surface)',
                                        border: '1px solid var(--v2-border-subtle)',
                                    }}
                                >
                                    <span
                                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                        style={{ background: 'var(--v2-primary-500,#3b82f6)' }}
                                    />
                                    <span className="text-[12.5px] font-bold" style={{ color: 'var(--v2-text-secondary)' }}>
                                        Page {currentPage}
                                    </span>
                                </div>

                                {/* Right — Prev / Next */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={handlePrevious}
                                        disabled={pageIndex === 0}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12.5px] font-semibold transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                                        style={{
                                            outline: 'none',
                                            background: 'var(--v2-bg-surface)',
                                            border: '1px solid var(--v2-border-card)',
                                            color: 'var(--v2-text-secondary)',
                                        }}
                                    >
                                        <ChevronLeft className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">Previous</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleNext}
                                        disabled={!hasMore}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12.5px] font-semibold transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                                        style={{
                                            outline: 'none',
                                            background: !hasMore ? 'var(--v2-bg-surface)' : 'var(--v2-primary-600,#2563eb)',
                                            border: !hasMore ? '1px solid var(--v2-border-card)' : '1px solid transparent',
                                            color: !hasMore ? 'var(--v2-text-muted)' : '#ffffff',
                                            boxShadow: !hasMore ? 'none' : '0 2px 8px rgba(37,99,235,0.30)',
                                        }}
                                    >
                                        <span className="hidden sm:inline">Next</span>
                                        <ChevronRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <RolesTab search={roleSearch} createTriggerRef={rolesCreateRef} onRolesChanged={fetchRoleNames} />
            )}

            {/* ══ DELETE CONFIRMATION DIALOG — mirrors patientsHistory.tsx exactly ══ */}
            <Dialog
                isOpen={!!memberToDelete}
                onClose={() => !isDeleting && setMemberToDelete(null)}
                size="small"
                showCloseButton={false}
                preventCloseOnOverlayClick={isDeleting}
                footer={
                    <div className="flex items-center justify-end gap-3">
                        <V2Button
                            variant="secondary"
                            size="small"
                            onClick={() => setMemberToDelete(null)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </V2Button>
                        <V2Button
                            variant="danger"
                            size="small"
                            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                            onClick={handleDeleteMember}
                            loading={isDeleting}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Removing…' : 'Remove Member'}
                        </V2Button>
                    </div>
                }
            >
                {memberToDelete && (
                    <div className="flex flex-col items-center text-center gap-4 py-2">
                        {/* Animated danger icon — same as Patient History */}
                        <div
                            className="relative flex h-16 w-16 items-center justify-center rounded-full"
                            style={{ background: 'rgba(239,68,68,0.10)' }}
                        >
                            <div
                                className="absolute inset-0 rounded-full animate-ping"
                                style={{ background: 'rgba(239,68,68,0.15)' }}
                            />
                            <AlertTriangle className="relative h-7 w-7" style={{ color: '#ef4444' }} />
                        </div>

                        {/* Heading */}
                        <div>
                            <h3 className="text-[18px] font-bold text-[var(--v2-text-heading)] mb-1">
                                Remove Member
                            </h3>
                            <p className="text-[14px] text-[var(--v2-text-muted)] leading-relaxed">
                                This action is permanent and cannot be undone.
                            </p>
                        </div>

                        {/* Member identity card — like Patient History patient card */}
                        <div
                            className="w-full flex items-center gap-3 rounded-xl px-4 py-3 border"
                            style={{
                                background: 'var(--v2-bg-card-inner)',
                                borderColor: 'var(--v2-border-subtle)',
                            }}
                        >
                            <PatientAvatar
                                name={memberToDelete.name || memberToDelete.email || ''}
                                mrn={memberToDelete.rad_id}
                            />
                            <div className="text-left min-w-0">
                                <p className="font-semibold text-[15px] truncate text-[var(--v2-text-primary)]">
                                    {memberToDelete.name || 'Unknown Member'}
                                </p>
                                <p className="text-[13px] text-[var(--v2-text-muted)] truncate">
                                    {memberToDelete.email}
                                </p>
                            </div>
                        </div>

                        {/* Warning note */}
                        <p className="text-[13px] text-[var(--v2-text-muted)]">
                            Are you sure you want to permanently remove{' '}
                            <span className="font-semibold text-[var(--v2-text-primary)]">
                                {memberToDelete.name || 'this member'}
                            </span>
                            ? They will lose all access to the platform.
                        </p>
                    </div>
                )}
            </Dialog>

            {/* ══ ADD MEMBER DIALOG ══ */}
            <Dialog
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                size="large"
                title="Add New Member"
                description="Invite a teammate by email. First/last name are optional."
            >
                <FormComponent
                    formState={formState as any}
                    onSubmit={handleAddMember}
                    loading={appointLoading}
                    onClose={() => setIsModalOpen(false)}
                    initialData={null}
                />
            </Dialog>
        </div>
    );
};

export default TeamManagement;
